import { Button, Field, FieldSlots, List, ListItem, makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger, SearchBox } from '@fluentui/react-components';
import { ChevronDown20Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { Country } from '../../../models';
import './CountrySelectionDropdown.scss';

interface CountrySelectionDropdownProps extends Pick<FieldSlots, | 'validationMessage' | 'hint'> {
  id: string;
  associatedCountries: Country[];
  label?: string;
  required?: boolean;
}

const useStyles = makeStyles({
  popoverSurface: { width: '100%', maxWidth: '416px' },
  popoverSurfaceList: { display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '12px' },
  popoverSurfaceSearchBox: { width: '100%', },
  errorBorder: { border: '1px solid #bc2f32', },
});

const CountrySelectionDropdownImpl: React.FC<CountrySelectionDropdownProps> = (props) => {
  const {
    id,
    label,
    associatedCountries,
    required,
    hint,
    validationMessage,
  } = props;

  const {
    countryStore,
  } = useStores();

  const searchBoxRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [sortedCountries, setSortedCountries] = useState<Country[]>([]);
  const [searchText, setSearchText] = useState('');

  const maxAssociatedCountries = 5;

  const isCountrySelected = (country: Country): boolean => {
    return associatedCountries.some(associatedCountry => associatedCountry.countryCode === country.countryCode);
  }

  const isCountryListItemDisabled = (country: Country): boolean => {
    return associatedCountries.length > 0 && associatedCountries.length >= maxAssociatedCountries && !isCountrySelected(country);
  }

  const onCountryCheckedChange = (checkedIds: string[]): void => {
    const filteredAssociatedCountries = sortedCountries.filter((country) => checkedIds.includes(`${country.countryId}`));
    associatedCountries.splice(0, associatedCountries.length, ...filteredAssociatedCountries);
  }

  const debouncedSearch = useDebouncedCallback(
    (searchText: string) => {
      setSearchText(searchText)
    },
    100
  );

  const filteredCountries = useMemo(() => {
    return sortedCountries.filter((country) => country.displayName.toLowerCase().includes(searchText.toLowerCase()));
  }, [searchText, sortedCountries.join()]);

  useEffect(() => {
    if (!countryStore.awardCountries) return;
    const na = countryStore.awardCountries.find(country => country.countryCode === 'N/A');
    const availableCountries = countryStore.awardCountries.filter(country => country.countryCode !== na?.countryCode);
    setSortedCountries(availableCountries);
  }, [countryStore.awardCountries.join()]);

  return (
    <Popover
      positioning='below-start'
    >
      <Field
        required={required}
        label={label ? {
          htmlFor: `${id}-input`,
          children: label,
          className: commonStyles.inputFieldLabel,
        } : undefined}
        hint={hint}
        validationMessage={validationMessage}
        validationMessageIcon={null}
        className='country-selection-dropdown'
      >
        <PopoverTrigger>
          <Button
            id={`${id}-input`}
            role='combobox'
            appearance='outline'
            className={mergeClasses(commonStyles.inputField, validationMessage ? styles.errorBorder : '', 'popover-trigger-button')}
            icon={<ChevronDown20Regular color='#616161' />}
            iconPosition='after'
            value={associatedCountries.map((country) => `${country.displayName}`).join(', ')}
          >
            {associatedCountries.map((country) => `${country.displayName}`).join(', ')}
          </Button>
        </PopoverTrigger>
      </Field>
      <PopoverSurface
        className={styles.popoverSurface}
      >
        <SearchBox
          ref={searchBoxRef}
          className={styles.popoverSurfaceSearchBox}
          autoComplete=''
          onChange={(_, data) => debouncedSearch(data.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              if (listRef.current === null) return;
              listRef.current.querySelector('li')?.focus();
            }
          }}
        />
        <List
          ref={listRef}
          navigationMode={filteredCountries.length ? 'items' : undefined}
          selectionMode={filteredCountries.length ? 'multiselect' : undefined}
          defaultSelectedItems={associatedCountries.map((country) => `${country.countryId}`)}
          className={styles.popoverSurfaceList}
          onSelectionChange={(_, data) => onCountryCheckedChange(data.selectedItems as string[])}
        >
          {
            filteredCountries.length ? filteredCountries.map((country) => (
              <ListItem
                key={`${country.countryId}`}
                value={`${country.countryId}`}
                checkmark={{ 'aria-label': country.displayName }}
                disabledSelection={isCountryListItemDisabled(country)}
                onKeyDown={(event) => {
                  if (!(event.key.length === 1 || event.key === 'Backspace') || searchBoxRef.current === null) return;
                  searchBoxRef.current.focus();
                }}
              >
                {country.displayName}
              </ListItem>
            )) : (
              <ListItem>
                No country found
              </ListItem>
            )
          }
        </List>
      </PopoverSurface>
    </Popover>
  );
}

export const CountrySelectionDropdown = observer(CountrySelectionDropdownImpl);
